import React, { useState, useEffect } from 'react';
import {
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { NotificationsActive as NotifyIcon, Send as SendIcon } from '@mui/icons-material';
import logger from '../../utils/logger';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const shortDateTime = (iso) => new Date(iso).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

/**
 * "Notify Customer" action for a job: emails the customer that their visit is
 * scheduled (date, time, address, team first names). The dialog previews the
 * email, shows what was already sent (confirmations and the automatic
 * reminder sent ~24 h before), and lets the admin change the recipient.
 *
 * Props:
 *   jobId      – required
 *   jobNumber  – optional, for labels
 *   compact    – render as an icon button instead of a text button
 *   onSent     – callback({ recipient }) after a successful send
 */
const NotifyCustomerButton = ({ jobId, jobNumber, compact = false, size = 'small', variant = 'outlined', onSent, disabled = false }) => {
  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);
  const [recipient, setRecipient] = useState('');

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    if (!open || !jobId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setSent(null);
    fetch(`${apiBase}/api/jobs/${jobId}/customer-notifications`, { headers: headers() })
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Failed to load the job');
        return json;
      })
      .then((json) => {
        if (cancelled) return;
        setData(json);
        setRecipient(json.preview?.email || '');
      })
      .catch((err) => {
        logger.error('[NotifyCustomerButton] load failed:', err);
        if (!cancelled) setError(err.message || 'Failed to load the job');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  const handleSend = async () => {
    if (!EMAIL_RE.test(recipient)) {
      setError('Enter a valid recipient email');
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/jobs/${jobId}/customer-notifications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers() },
        body: JSON.stringify({ to: recipient }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to notify the customer');
      setSent(json);
      if (onSent) onSent(json);
    } catch (err) {
      logger.error('[NotifyCustomerButton] send failed:', err);
      setError(err.message || 'Failed to notify the customer');
    } finally {
      setSending(false);
    }
  };

  const close = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!sending) setOpen(false);
  };

  const openDialog = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setOpen(true);
  };

  const preview = data?.preview;
  const history = data?.history || [];
  const confirmations = history.filter((h) => h.kind === 'confirmation');
  // Missing email isn't blocking: the admin can type one in
  const blocking = preview && !preview.ready && !/email/i.test(preview.problem || '') ? preview.problem : null;
  const label = confirmations.length ? 'Notify Again' : 'Notify Customer';

  return (
    <>
      {compact ? (
        <Tooltip title="Notify customer (visit confirmation)">
          <span>
            <IconButton size={size} onClick={openDialog} disabled={disabled} color="primary">
              <NotifyIcon fontSize={size === 'small' ? 'small' : 'medium'} />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button size={size} variant={variant} startIcon={<NotifyIcon />} onClick={openDialog} disabled={disabled}>
          Notify Customer
        </Button>
      )}

      <Dialog open={open} onClose={close} maxWidth="sm" fullWidth onClick={(e) => e.stopPropagation()}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotifyIcon color="primary" />
          {label}{jobNumber ? ` · ${jobNumber}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
          ) : sent ? (
            <Alert severity="success">Confirmation sent to <strong>{sent.recipient}</strong>.</Alert>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
              {blocking && <Alert severity="warning">{blocking}</Alert>}

              {preview && preview.when && (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="caption" color="text.secondary">Subject</Typography>
                  <Typography variant="body2" fontWeight={600} gutterBottom>{preview.subject}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>Hi {preview.firstName},</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>Your cleaning is scheduled. Here are the details of your visit:</Typography>
                  <Box component="table" sx={{ fontSize: 14, '& td': { pr: 2, py: 0.25, verticalAlign: 'top' }, '& td:first-of-type': { color: 'text.secondary' } }}>
                    <tbody>
                      <tr><td>When</td><td><strong>{preview.when}</strong></td></tr>
                      {preview.addressText && <tr><td>Address</td><td><strong>{preview.addressText}</strong></td></tr>}
                      {preview.serviceLabel && <tr><td>Service</td><td><strong>{preview.serviceLabel}</strong></td></tr>}
                      <tr><td>Your team</td><td><strong>{preview.team || '—'}</strong></td></tr>
                    </tbody>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    [View my appointment] · Need to change something? Reply to this email or contact us.
                  </Typography>
                </Box>
              )}

              {confirmations.length > 0 && (
                <Alert severity="info">
                  The customer was already notified {confirmations.length === 1 ? 'once' : `${confirmations.length} times`}.
                </Alert>
              )}

              {history.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>History</Typography>
                  {history.map((h, i) => (
                    <Typography key={i} variant="caption" display="block" color="text.secondary">
                      {h.kind === 'reminder' ? 'Reminder (automatic)' : 'Confirmation'} sent {shortDateTime(h.sentAt)} to {h.recipient}
                    </Typography>
                  ))}
                </Box>
              )}

              <TextField
                label="Send to"
                type="email"
                size="small"
                fullWidth
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                helperText={preview?.email ? 'Customer email on file. Change it to send elsewhere.' : 'No email on file for this customer. Enter one.'}
                error={!!recipient && !EMAIL_RE.test(recipient)}
              />
              <Typography variant="caption" color="text.secondary">
                A reminder is also emailed automatically about 24 hours before the visit.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={sending}>{sent ? 'Done' : 'Cancel'}</Button>
          {!sent && (
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={loading || sending || !preview || Boolean(blocking) || !EMAIL_RE.test(recipient)}
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            >
              {sending ? 'Sending...' : label}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotifyCustomerButton;
